import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  PickupCreateRequest,
  pickupCreateSchema,
  pickupUpdateSchema,
  type Pickup,
  type PickupCreateFormValues,
  type PickupUpdateFormValues,
} from "@/types/pickup-type";
import { createPickup, updatePickup } from "@/api/pickup-api";
import { useToast } from "@/hooks/use-toast";
import { SaveButton } from "@/components/ui/buttons/save-button";
import { useLocation } from "wouter";
import { DateTimePicker } from "@/components/ui/datetime-picker";

type FormMode = "create" | "update";

interface PickupFormProps {
  mode: FormMode;
  initialValues?: Partial<Pickup> & { id?: string };
  onSuccess?: (saved: Pickup) => void;
  onCancel?: () => void;
}

const pickupStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "ready", label: "Ready" },
  { value: "picked_up", label: "Picked Up" },
  { value: "cancelled", label: "Cancelled" },
];

export function PickupForm({
  mode,
  initialValues,
  onSuccess,
  onCancel,
}: PickupFormProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const isCreate = mode === "create";

  const resolver = zodResolver(
    isCreate ? pickupCreateSchema : pickupUpdateSchema
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<PickupCreateFormValues | PickupUpdateFormValues>({
    resolver,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      orderId: initialValues?.orderId ?? "",
      courierName: initialValues?.courierName ?? "",
      pickupStatus: initialValues?.pickupStatus ?? pickupStatusOptions[0].value,
      pickupTime: initialValues?.pickupTime ?? "",
      isActive: initialValues?.isActive ?? true,
    },
  });

  const onSubmit: SubmitHandler<
    PickupCreateFormValues | PickupUpdateFormValues
  > = async (values) => {
    try {
      let saved: any;
      const submitData = {
        ...values,
      };

      if (isCreate) {
        saved = await createPickup(submitData as PickupCreateRequest);
      } else {
        const id = (initialValues as any)?.id as string | undefined;
        if (!id) {
          throw new Error("Missing id for update");
        }
        saved = await updatePickup(id, submitData as PickupUpdateFormValues);
      }

      toast({
        title: isCreate ? "Created successfully" : "Updated successfully",
      });
      onSuccess?.(saved?.data ?? saved);
      if (!onSuccess) {
        setLocation("/pickups");
      }
    } catch (error: any) {
      toast({
        title: "Operation failed",
        description: error?.message ?? undefined,
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setLocation("/pickups");
    }
  };

  // Lightweight section wrapper for consistent spacing and headings
  const FormSection = React.useCallback(
    ({
      title,
      description,
      children,
    }: {
      title: string;
      description?: string;
      children: React.ReactNode;
    }) => (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: description ? 0.5 : 1 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </Box>
    ),
    []
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <FormSection title="Basic Information">
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Order ID"
                      placeholder="e.g. o123456"
                      InputLabelProps={{ shrink: true }}
                      {...register("orderId" as any)}
                      error={!!errors.orderId}
                      helperText={errors.orderId?.message as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Courier Name"
                      placeholder="e.g. Flash"
                      InputLabelProps={{ shrink: true }}
                      {...register("courierName" as any)}
                      error={!!errors.courierName}
                      helperText={errors.courierName?.message as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <FormControl fullWidth error={!!errors.pickupStatus}>
                      <InputLabel id="pickup-status-label">
                        Pickup Status
                      </InputLabel>
                      <Controller
                        name={"pickupStatus" as any}
                        control={control}
                        render={({ field }) => (
                          <Select
                            labelId="pickup-status-label"
                            label="Pickup Status"
                            value={(field.value as string) ?? ""}
                            onChange={field.onChange}
                          >
                            {pickupStatusOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.pickupStatus?.message && (
                        <FormHelperText>
                          {errors.pickupStatus.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <Controller
                      name={"pickupTime" as any}
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          value={field.value as string}
                          onChange={field.onChange}
                          label="Pickup Time"
                          placeholder="Select pickup date and time"
                          error={!!errors.pickupTime}
                          fullWidth
                        />
                      )}
                    />
                    {errors.pickupTime?.message && (
                      <FormHelperText sx={{ mt: 0.5, mx: 1.75 }}>
                        {errors.pickupTime.message as string}
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name={"isActive" as any}
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value as boolean}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 1,
                }}
              >
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  color="inherit"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <SaveButton
                  disabled={isSubmitting}
                  handleSave={handleSubmit(onSubmit)}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
}

export default PickupForm;
